import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link, Paper,
    Typography,
} from '@mui/material';
import SearchableSelect from '../common/SearchableSelect';
import { SegmentService } from '../../services/segment.service';
import { formatMoney, humanizeEnum } from '../../utils/format';

// W3-J: small pieces shared by the provisions page and the provident-fund page.

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const periodLabel = (year: number, month: number | null | undefined): string =>
    month ? `${MONTH_NAMES[month - 1]?.slice(0, 3) ?? month} ${year}` : String(year);

export const currentPeriod = (): { year: number; month: number } => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

/** Money, or a dash for null. */
export const money = (v: number | null | undefined): string => formatMoney(v);

/** Exact years: up to 4 decimals, no padding. */
export const years = (v: number | null | undefined): string =>
    v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 4 });

export const PeriodPicker: React.FC<{
    year: number; month: number; onChange: (p: { year: number; month: number }) => void; disabled?: boolean;
}> = ({ year, month, onChange, disabled }) => {
    const thisYear = new Date().getFullYear();
    const monthOptions = MONTH_NAMES.map((n, i) => ({ id: String(i + 1), label: n }));
    const yearOptions = Array.from({ length: 8 }, (_, i) => String(thisYear - 5 + i)).map(y => ({ id: y, label: y }));
    return (
        <>
            <SearchableSelect
                data-help="payroll.provisionPeriod.month" label="Month" size="small" options={monthOptions} value={String(month)} clearable={false} disabled={disabled}
                onChange={id => id && onChange({ year, month: Number(id) })} getOptionLabel={o => o.label}
                sx={{ flex: '1 1 160px', maxWidth: 220 }}
            />
            <SearchableSelect
                data-help="payroll.provisionPeriod.year" label="Year" size="small" options={yearOptions} value={String(year)} clearable={false} disabled={disabled}
                onChange={id => id && onChange({ year: Number(id), month })} getOptionLabel={o => o.label}
                sx={{ flex: '1 1 120px', maxWidth: 160 }}
            />
        </>
    );
};

/** Resolves segment ids to names with the segment service the app already uses. Null = default segment. */
export const useSegmentNames = (institutionId: string): ((id: string | null | undefined) => string) => {
    const [names, setNames] = useState<Record<string, string>>({});
    useEffect(() => {
        if (!institutionId) return undefined;
        let cancelled = false;
        SegmentService.getByInstitution(0, 500)
            .then((page: any) => {
                if (cancelled) return;
                const map: Record<string, string> = {};
                (page?.content ?? []).forEach((s: { id?: string; name: string }) => { if (s.id) map[s.id] = s.name; });
                setNames(map);
            })
            .catch(() => { /* names are cosmetic: fall back to the id */ });
        return () => { cancelled = true; };
    }, [institutionId]);
    return useMemo(
        () => (id: string | null | undefined) => (id ? names[id] ?? id.slice(0, 8) : 'Default / unassigned'),
        [names],
    );
};

export const RunStatusChip: React.FC<{ status: string }> = ({ status }) => (
    <Chip size="small" color={status === 'POSTED' ? 'success' : 'default'} label={humanizeEnum(status)} />
);

export const JournalLink: React.FC<{ id: string | null | undefined; label?: string }> = ({ id, label = 'View JE' }) =>
    id ? <Link component={RouterLink} to={`/accounting/journal-entries/${id}`}>{label}</Link> : <>—</>;

export const SummaryStats: React.FC<{ items: { label: string; value: string }[] }> = ({ items }) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, my: 2 }}>
        {items.map(i => (
            <Paper key={i.label} variant="outlined" sx={{ p: 1.5, flex: '1 1 180px', maxWidth: 280 }}>
                <Typography variant="caption" color="text.secondary">{i.label}</Typography>
                <Typography variant="h6">{i.value}</Typography>
            </Paper>
        ))}
    </Box>
);

/** Blocker (red, disables Post upstream) and warnings (amber) of a preview. */
export const PreviewNotices: React.FC<{ blocker: string | null; warnings: string[] }> = ({ blocker, warnings }) => (
    <>
        {blocker && <Alert severity="error" sx={{ mb: 1 }}>{blocker}</Alert>}
        {warnings.map((w, i) => <Alert key={i} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
    </>
);

export const ConfirmDialog: React.FC<{
    open: boolean; title: string; message: React.ReactNode; confirmLabel: string; busy?: boolean;
    onConfirm: () => void; onClose: () => void;
}> = ({ open, title, message, confirmLabel, busy, onConfirm, onClose }) => (
    <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.provisionConfirm.page' } as any} maxWidth="xs" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent data-help-section="payroll.provisionConfirm.section.message"><DialogContentText component="div">{message}</DialogContentText></DialogContent>
        <DialogActions>
            <Button data-help="payroll.provisionConfirm.cancel" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button data-help="payroll.provisionConfirm.confirm" color="error" variant="contained" onClick={onConfirm} disabled={busy}>{confirmLabel}</Button>
        </DialogActions>
    </Dialog>
);
